import { Nillable } from '@wyz/types';
import { invariant } from '@wyz/utils';
import { isNil } from 'lodash-es';
import { ISupplierTypeOptionItem, IVehicleTypeGroupList } from '~/common/types';
import { DIAMETERS, DIAMETER_OPERATORS } from '~/purchase/schemas';
import {
  IDiameter,
  IDiameterPayload,
  IPolicyDefinitionValue,
} from '~/purchase/types';

export const NO_REFINEMENT_VALUE = 'ALL';

export const buildVehicleTypeLookup = (
  vehicleTypeGroups: IVehicleTypeGroupList['data'],
) => {
  const vehicleTypeLookup: Record<string, IPolicyDefinitionValue> = {};
  const vehicleTypeGroupLookup: Record<string, IPolicyDefinitionValue> = {};
  vehicleTypeGroups.forEach((vehicleTypeGroup) => {
    vehicleTypeGroupLookup[vehicleTypeGroup.publicKey] = {
      key: vehicleTypeGroup.publicKey,
      name: vehicleTypeGroup.name,
    };
    vehicleTypeGroup.vehicleTypes.forEach((vehicleType) => {
      vehicleTypeLookup[vehicleType.publicKey] = {
        key: String(vehicleType.publicKey),
        name: vehicleType.name,
      };
    });
  });
  return { vehicleTypeGroupLookup, vehicleTypeLookup };
};

export const buildSupplierTypeLookup = (
  supplierTypes: Array<ISupplierTypeOptionItem>,
) => {
  return supplierTypes.reduce(
    (acc, curr) => {
      acc[curr.value] = { key: String(curr.id), name: curr.label };
      return acc;
    },
    {} as Record<string, IPolicyDefinitionValue>,
  );
};

// Overloaded function to throw only if fail is true

export function getLookupValue(
  key: string,
  lookup: Record<string, IPolicyDefinitionValue>,
): IPolicyDefinitionValue;

export function getLookupValue(
  key: string,
  lookup: Record<string, IPolicyDefinitionValue>,
  fail: true,
): IPolicyDefinitionValue;

export function getLookupValue(
  key: string,
  lookup: Record<string, IPolicyDefinitionValue>,
  fail: false,
): IPolicyDefinitionValue | undefined;

// implementation
export function getLookupValue(
  key: string,
  lookup: Record<string, IPolicyDefinitionValue>,
  fail = true,
) {
  const value = lookup[key] ?? undefined;
  if (fail) {
    invariant(value, JSON.stringify({ key, lookup }));
  }
  return value;
}

export const CUSTOMER_CRITERIA_CODE = 'CUSTOMER_ID';

export const guessNextDiameter = (
  alreadySelectedDiameters: Array<number>,
  alreadySelectedOperators: Array<string>,
): { diameter?: number; operator?: IDiameter['operator'] } => {
  if (alreadySelectedDiameters.length === 0) {
    return { diameter: 16, operator: DIAMETER_OPERATORS.LOWER_THAN_OR_EQUAL };
  }

  const maxDiameter = Math.max(...alreadySelectedDiameters);
  let guessDiameter = undefined;
  for (let i = 0; i < DIAMETERS.length; i++) {
    if (DIAMETERS[i] > maxDiameter) {
      guessDiameter = DIAMETERS[i];
      break;
    }
  }
  let guessOperator = undefined;
  if (alreadySelectedOperators.length === 1) {
    if (
      alreadySelectedOperators[0] === DIAMETER_OPERATORS.LOWER_THAN_OR_EQUAL
    ) {
      guessOperator = DIAMETER_OPERATORS.GREATER_THAN_OR_EQUAL;
    } else if (
      alreadySelectedOperators[0] === DIAMETER_OPERATORS.GREATER_THAN_OR_EQUAL
    ) {
      guessOperator = DIAMETER_OPERATORS.LOWER_THAN_OR_EQUAL;
    }
  }
  return { diameter: guessDiameter, operator: guessOperator };
};

export const extractSelectedDiameters = (
  refinements: Nillable<
    Array<
      Nillable<{
        diameter?: Nillable<IDiameter>;
      }>
    >
  >,
) => {
  const allDiameters: Array<number> = [];
  const allOperators: Array<string> = [];
  (refinements ?? []).forEach((item) => {
    if (!isNil(item?.diameter?.from)) {
      allDiameters.push(item.diameter.from);
    }
    if (!isNil(item?.diameter?.to)) {
      allDiameters.push(item.diameter.to);
    }
    if (item?.diameter?.operator) {
      allOperators.push(item.diameter.operator);
    }
  });
  return {
    diameters: allDiameters,
    operators: allOperators,
  };
};

export const getDiameterPayload = (
  diameter?: IDiameter | null,
): IDiameterPayload | null => {
  if (!diameter) {
    return null;
  }

  const { from, to, operator } = diameter;

  switch (operator) {
    case DIAMETER_OPERATORS.LOWER_THAN_OR_EQUAL:
      return { from: Math.min(...DIAMETERS), to: to as number, operator };
    case DIAMETER_OPERATORS.GREATER_THAN_OR_EQUAL:
      return { from: from as number, to: Math.max(...DIAMETERS), operator };
    case DIAMETER_OPERATORS.BETWEEN:
      return { from: from as number, to: to as number, operator };
    case DIAMETER_OPERATORS.EQUAL:
      return { from: from as number, to: from as number, operator };
    default:
      return null;
  }
};
